import { Injectable, inject } from '@angular/core';
import { SnowplowService } from '@sirius/common';
import {
    Question,
    trackCustomerMatchmakerStepCompleteSpec,
    trackCustomerMatchmakerStepStartSpec,
    trackCustomerMatchmakerButtonClickSpec,
} from '@urweb/snowtype';
import { MatchmakingFormConfig, Step } from 'matchmaking/models';
import { MatchmakingDataService } from './matchmaking-data.service';

interface StepCompleteAction {
    step_number: number;
    step_name?: string;
    questions?: Question[];
}

interface StepStartAction {
    step_number: number;
    step_name?: string;
}

interface ButtonClickAction {
    identifier: string;
    element_content?: string;
    element_classes?: string[];
}

interface VentionTracking {
    vention_user?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class TrackingMatchmakingSnowplowService {
    private snowplowService = inject(SnowplowService);
    private dataService = inject(MatchmakingDataService);


    public trackStepComplete({ step_number }: StepCompleteAction, config: MatchmakingFormConfig) {
        const questions = this.getTrackingQuestionsForStep(config.currentStep, config);
        let ventionTracking: VentionTracking = {};

        if (step_number === 4) {
            ventionTracking = {
                vention_user: false,
            };
        }

        this.snowplowService.track({
            fn: (data) =>
                trackCustomerMatchmakerStepCompleteSpec({
                    action: 'completed',
                    step_number: step_number * 10, // Double digits allows for more dynamic step tracking (example 10.1)
                    questions: questions,
                    wizard_name: 'customer_matchmaker',
                    step_name: config.currentStep,
                    context: data,
                    timestamp: new Date().getTime(),
                    ...ventionTracking,
                }),
            data: [],
        });
    }

    public trackStepStart({ step_number, step_name }: StepStartAction) {
        this.snowplowService.track({
            fn: (data) =>
                trackCustomerMatchmakerStepStartSpec({
                    step_name: step_name,
                    action: 'started',
                    step_number: step_number * 10,
                    wizard_name: 'customer_matchmaker',
                    context: data,
                    timestamp: new Date().getTime(),
                }),
            data: [],
        });
    }

    public trackButtonClick({ identifier, element_content, element_classes }: ButtonClickAction) {
        this.snowplowService.track({
            fn: (data) =>
                trackCustomerMatchmakerButtonClickSpec({
                    identifier,
                    element_content: element_content,
                    element_classes: element_classes,
                    context: data,
                    timestamp: new Date().getTime(),
                }),
            data: [],
        });
    }

    private getTrackingQuestionsForStep(step: Step, config: MatchmakingFormConfig): Question[] {
        switch (step) {
            case 'industry':
                return this.trackStepIndustry();
            case 'application':
                return this.trackStepApplication();
            case 'application_specific':
                return this.trackStepApplicationSpecific(config);
            case 'project':
                return this.trackStepProject(config);
            case 'preview':
                return this.trackStepPreview(config);
            default:
                return [];
        }
    }

    private trackStepPreview(config: MatchmakingFormConfig): Question[] {
        const questions = [
            { question_id: 'work_email', response: config.workEmail },
            { question_id: 'first_name', response: config.firstName },
            { question_id: 'last_name', response: config.lastName },
            { question_id: 'company', response: config.company },
            { question_id: 'phone', response: config.phoneNo },
            { question_id: 'country', response: config.country },
            { question_id: 'job_title', response: config.worktitle },
            { question_id: 'user_role', response: config.userRole },
        ];

        if (config.region) {
            questions.push({ question_id: 'region', response: config.region });
        }

        return questions;
    }

    private trackStepProject(config: MatchmakingFormConfig): Question[] {
        return [
            { question_id: 'decision_maker', response: config.decisionMaker },
            { question_id: 'project_goal', response: config.projectGoal },
            { question_id: 'budget_range', response: config.budgetRange },
            { question_id: 'timeline', response: config.timeline },
        ];
    }

    private trackStepApplicationSpecific(config: MatchmakingFormConfig): Question[] {
        if (config.stepApplicationSpecific.customPage) {
            const questionIdMapping: { [key: string]: string } = {
                question1: '_shape',
                question2: '_weight',
                question3: '_speed',
                question4: '_integration',
                question5: '_support',
                question6: '_other',
            };

            const questionAnswers = Object.entries(config.stepApplicationSpecific.questions)
                .filter(([_, questionValue]) => questionValue)
                .map(([questionKey, questionValue]) => ({
                    question_id:
                        this.dataService.applicationAsProperty() + questionIdMapping[questionKey],
                    response: questionValue,
                }));
            return questionAnswers;
        }

        return [
            {
                question_id: 'application_specific_other',
                response: config.stepApplicationSpecific.otherText,
            },
        ];
    }

    private trackStepApplication(): Question[] {
        return [
            {
                question_id: 'application_selection',
                response: this.dataService.selectedApplicationTitle(),
            },
        ];
    }

    private trackStepIndustry(): Question[] {
        return [
            {
                question_id: 'industry_selection',
                response: this.dataService.selectedIndustryTitle(),
            },
        ];
    }
}
